import { checkType, CtyShowToast, type AddFieldDraft } from '@circuitry-ai/doc-ui';
import { toObject } from './agentutils';

export type JsonPrimitive = string | number | boolean | null;
export type JsonObject = { [key: string]: JsonValue };
export type JsonArray = JsonValue[];
export type JsonValue = JsonPrimitive | JsonObject | JsonArray;

const EMPTY_ACTIONS_CONFIG: Record<string, JsonValue> = {};

export const isObjectValue = (value: JsonValue): value is JsonObject =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

export const isPrimitiveValue = (value: JsonValue): value is JsonPrimitive =>
    typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean' || value === null;

export const areJsonValuesEqual = (left: unknown, right: unknown): boolean =>
    JSON.stringify(left) === JSON.stringify(right);

export const getByPath = (source: JsonValue, path: string[]): JsonValue => {
    let current: any = source;
    for (const segment of path) {
        current = current?.[segment];
    }
    return current as JsonValue;
};

export const setByPath = (source: JsonValue, path: string[], nextValue: JsonValue): JsonValue => {
    if (path.length === 0) return nextValue;
    const [head, ...tail] = path;
    if (!isObjectValue(source)) return source;
    return {
        ...source,
        [head]: setByPath((source as JsonObject)[head], tail, nextValue),
    };
};

export const deleteByPath = (source: JsonValue, path: string[]): JsonValue => {
    if (!isObjectValue(source) || path.length === 0) return source;
    const [head, ...tail] = path;
    if (tail.length === 0) {
        const next = { ...source };
        delete next[head];
        return next;
    }
    return {
        ...source,
        [head]: deleteByPath((source as JsonObject)[head], tail),
    };
};

export const stringifyArrayEntry = (entry: JsonValue): string => {
    if (entry == null) return '';
    if (isPrimitiveValue(entry)) return String(entry);
    return JSON.stringify(entry);
};

const omitConfigurationMeta = (source: Record<string, JsonValue>): Record<string, JsonValue> => {
    const next: Record<string, JsonValue> = {};
    for (const [key, value] of Object.entries(source ?? {})) {
        if (key === 'configuration_id') continue;
        next[key] = value;
    }
    return next;
};

export const DEFAULT_ADD_FIELD_DRAFT = (): AddFieldDraft => ({
    keyName: '',
    configId: '',
    fieldType: 'string',
    stringValue: '',
    numberValue: '',
    booleanValue: false,
    objectValue: '{}',
});

type AgentQueryFilters = {
    aiworker_id: string;
    config_name: string;
    status?: string;
    is_active?: boolean;
    config_id?: string;
    version?: number | string;
    version_id?: string;
};

export const buildAgentQueryPayload = (
    aiworkerId: string,
    configId?: string,
    configVersion?: number | string,
    configVersionId?: string
) => {
    const filters: AgentQueryFilters = {
        aiworker_id: aiworkerId,
        config_name: 'agent_config',
    };
    if (configId) {
        filters.config_id = configId;
        if (configVersion !== undefined && configVersion !== null && configVersion !== '') {
            filters.version = configVersion;
        }
        if (configVersionId) {
            filters.version_id = configVersionId;
        }
    } else {
        filters.status = 'active';
        filters.is_active = true;
    }
    return {
        scope: 'tenant',
        pagination: { page: 1, per_page: 10 },
        filters,
    };
};

export const buildAgentConfigurationFallbackPayload = (aiworkerId: string) => ({
    scope: 'tenant',
    pagination: { page: 1, per_page: 10 },
    filters: {
        aiworker_id: aiworkerId,
        config_name: 'agent_config',
    },
});

export const extractConfigurationsFromResponse = (configurationData: unknown) => {
    const rawConfigurationData = configurationData as {
        configurations?: Record<string, unknown>[];
        data?: { configurations?: Record<string, unknown>[]; data?: { configurations?: Record<string, unknown>[] } };
    };
    return (
        rawConfigurationData?.configurations ??
        rawConfigurationData?.data?.configurations ??
        rawConfigurationData?.data?.data?.configurations ??
        []
    );
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export const resolveConfigId = (draft: AddFieldDraft, t: (key: string) => string): string | null => {
    const trimmedConfigId = draft.configId.trim();
    if (!trimmedConfigId) {
        CtyShowToast(checkType.error, t('Config ID is required.'));
        return null;
    }
    if (!UUID_PATTERN.test(trimmedConfigId)) {
        CtyShowToast(checkType.error, t('Enter a valid UUID for Config ID.'));
        return null;
    }
    return trimmedConfigId;
};

export const resolveAddFieldValue = (draft: AddFieldDraft, t: (key: string) => string): JsonValue | null => {
    if (draft.fieldType === 'boolean') {
        return draft.booleanValue;
    }
    if (draft.fieldType === 'number') {
        return Number(draft.numberValue || 0);
    }
    if (draft.fieldType === 'object') {
        const parsed = toObject(draft.objectValue);
        if (Object.keys(parsed).length === 0 && draft.objectValue.trim() !== '{}' && draft.objectValue.trim() !== '') {
            CtyShowToast(checkType.error, t('Enter a valid JSON object.'));
            return null;
        }
        return parsed;
    }
    return draft.stringValue;
};

export const parseAgentConfigurationSections = (selectedConfiguration: Record<string, any> | undefined) => {
    const rootConfigurationId = selectedConfiguration?.configuration_id ?? '';
    const agentConfig = toObject(selectedConfiguration?.config);

    const botData = toObject(agentConfig?.bot_data);
    const isLegacyBotData = Boolean(botData?.config && typeof botData?.config === 'object');
    const botConfig = (isLegacyBotData ? toObject(botData?.config) : botData) as Record<string, JsonValue>;
    const botPathPrefix = isLegacyBotData ? 'config' : 'config.bot_data';
    const botDeletePathPrefix = isLegacyBotData ? 'bot_data.config' : 'bot_data';
    const botConfigurationId = botData?.configuration_id ?? rootConfigurationId;

    return {
        rootConfigurationId,
        botConfig: botConfig ?? EMPTY_ACTIONS_CONFIG,
        botPathPrefix,
        botDeletePathPrefix,
        botConfigurationId,
    };
};

export const parseWorkflowConfigurationSections = (selectedConfiguration: Record<string, any> | undefined) => {
    const rootConfigurationId = selectedConfiguration?.configuration_id ?? '';
    const workflowData = toObject(toObject(selectedConfiguration?.config)?.workflow_data);
    const isLegacyWorkflowData = Boolean(workflowData?.config && typeof workflowData?.config === 'object');
    const workflowConfig = (isLegacyWorkflowData ? toObject(workflowData?.config) : workflowData ?? EMPTY_ACTIONS_CONFIG) as Record<string, JsonValue>;

    return {
        rootConfigurationId,
        workflowConfig,
        workflowPathPrefix: isLegacyWorkflowData ? 'config' : 'config.workflow_data',
        workflowDeletePathPrefix: isLegacyWorkflowData ? 'workflow_data.config' : 'workflow_data',
        workflowConfigurationId: workflowData?.configuration_id ?? rootConfigurationId,
    };
};

export const parseDifyConfigurationSections = (selectedConfiguration: Record<string, any> | undefined) => {
    const rootConfigurationId = selectedConfiguration?.configuration_id ?? '';
    const difyData = toObject(toObject(selectedConfiguration?.config)?.dify_config);
    const isLegacyDifyData = Boolean(difyData?.config && typeof difyData?.config === 'object');
    const difyConfig = omitConfigurationMeta(
        (isLegacyDifyData ? toObject(difyData?.config) : difyData ?? EMPTY_ACTIONS_CONFIG) as Record<string, JsonValue>
    );

    return {
        rootConfigurationId,
        difyConfig,
        difyPathPrefix: isLegacyDifyData ? 'config' : 'config.dify_config',
        difyDeletePathPrefix: isLegacyDifyData ? 'dify_config.config' : 'dify_config',
        difyConfigurationId: difyData?.configuration_id ?? rootConfigurationId,
    };
};

export const buildNestedConfigSection = (
    path: string[],
    key: string,
    value: JsonValue
): Record<string, JsonValue> => {
    let nested: Record<string, JsonValue> = { [key]: value };
    for (let index = path.length - 1; index >= 0; index -= 1) {
        nested = { [path[index]]: nested };
    }
    return nested;
};
